import { useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { Dropdown, Menu, Tag } from 'antd';
import classnames from 'classnames';

import {
  fetchTeamMembersList,
  deletedMember,
  suspendMember,
  reactivateMember,
  reInviteMember
} from 'services/team-members';

import {
  MEMBER_PERMISSIONS_NAME,
  MEMBER_STATUS_CODES,
  MEMBER_STATUS_NAME_ID
} from 'constants/team-members';
import { userInfo } from 'utils/helpers';
import { useModal } from 'contexts/modalProvider.context';
import { getFullName } from 'utils/team-members';

import BRTable from 'components/BRNewTable/BRTable';
import { notify } from 'components/Notify/Notify';
import ConfirmationModal from 'components/BRModals/ConfirmationModal/ConfirmationModal';

import { ReactComponent as ActionsIcon } from 'assets/bosta-icons/More-Actions.svg';

import './TeamMembersTable.less';

const { ACTIVE, INVITATION_SENT, EMAIL_VERIFIED, SUSPENDED } =
  MEMBER_STATUS_CODES;

const TeamMembersTable = ({ setIsLoading, permissions = {} }) => {
  const { formatMessage } = useIntl();
  const refreshTable = useRef();
  const history = useHistory();
  const { openModal } = useModal();

  const [count, setCount] = useState(0);

  const updateTable = () => refreshTable.current();
  const { businessId } = userInfo?.businessAdminInfo || {};
  const userId = userInfo?._id;

  const {
    HIDE_ACTIONS_MENU,
    HIDE_DELETE_MEMBER,
    HIDE_SUSPEND_MEMBER,
    HIDE_REACTIVATE_MEMBER
  } = permissions;

  const getTableData = async ({ page, limit }) => {
    setIsLoading(true);

    const payload = {
      limit,
      page
    };

    try {
      const { list = [], total = 0 } = await fetchTeamMembersList(
        businessId,
        payload
      );

      setCount(total);
      setIsLoading(false);

      return {
        list,
        total
      };
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const renderTag = (state) => {
    const stateNameId = MEMBER_STATUS_NAME_ID[state?.code];

    return (
      <Tag className={`br-tag ${stateNameId}`}>
        {formatMessage({
          id: `team_members.status.${stateNameId}`
        })}
      </Tag>
    );
  };

  const handleDeleteMember = async (id) => {
    setIsLoading(true);

    try {
      await deletedMember(id);

      notify({
        msg: formatMessage({
          id: 'team_members.delete_member.confirmation_modal.success_msg'
        }),
        type: 'success'
      });

      updateTable();
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const confirmOnUserBeforeDelete = ({ profile, _id }) => {
    openModal(ConfirmationModal, {
      title: formatMessage({
        id: 'team_members.delete_member.confirmation_modal.title'
      }),
      content: formatMessage(
        {
          id: 'team_members.delete_member.confirmation_modal.message'
        },
        {
          memberName: getFullName(profile),
          break: (
            <>
              <br />
              <br />
            </>
          )
        }
      ),
      confirmButtonLabel: formatMessage({
        id: 'team_members.delete_member.confirmation_modal.confirm_btn'
      }),
      cancelButtonLabel: formatMessage({
        id: 'team_members.delete_member.confirmation_modal.cancel_btn'
      }),
      modalAction: () => handleDeleteMember(_id)
    });
  };

  const handleSuspendMember = async ({ memberName, id }) => {
    setIsLoading(true);

    try {
      await suspendMember(id);

      notify({
        msg: formatMessage(
          {
            id: 'team_members.suspend_member.confirmation_modal.success_msg'
          },
          { memberName }
        ),
        type: 'success'
      });

      updateTable();
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const confirmOnUserBeforeSuspend = ({ profile, _id }) => {
    const memberName = getFullName(profile);

    openModal(ConfirmationModal, {
      title: formatMessage(
        {
          id: 'team_members.suspend_member.confirmation_modal.title'
        },
        {
          memberName
        }
      ),
      content: formatMessage({
        id: 'team_members.suspend_member.confirmation_modal.message'
      }),
      confirmButtonLabel: formatMessage({
        id: 'team_members.suspend_member.confirmation_modal.confirm_btn'
      }),
      cancelButtonLabel: formatMessage({
        id: 'team_members.suspend_member.confirmation_modal.cancel_btn'
      }),
      modalAction: () => handleSuspendMember({ memberName, id: _id })
    });
  };

  const handleReactivateMember = async ({ memberName, id }) => {
    setIsLoading(true);

    try {
      await reactivateMember(id);

      notify({
        msg: formatMessage(
          {
            id: 'team_members.reactivate_member.confirmation_modal.success_msg'
          },
          { memberName }
        ),
        type: 'success'
      });

      updateTable();
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const confirmOnUserBeforeReactivate = ({ profile, _id }) => {
    const memberName = getFullName(profile);

    openModal(ConfirmationModal, {
      title: formatMessage(
        {
          id: 'team_members.reactivate_member.confirmation_modal.title'
        },
        {
          memberName
        }
      ),
      content: formatMessage({
        id: 'team_members.reactivate_member.confirmation_modal.message'
      }),
      confirmButtonLabel: formatMessage({
        id: 'team_members.reactivate_member.confirmation_modal.confirm_btn'
      }),
      cancelButtonLabel: formatMessage({
        id: 'team_members.reactivate_member.confirmation_modal.cancel_btn'
      }),
      modalAction: () => handleReactivateMember({ memberName, id: _id }),
      confirmButtonType: 'primary'
    });
  };

  const handleReInviteMember = async (id) => {
    setIsLoading(true);

    try {
      await reInviteMember(businessId, id);

      notify({
        msg: formatMessage({
          id: 'team_members.reinvite_member.success_msg'
        }),
        type: 'success'
      });
    } catch (error) {
      notify({ msg: error.message, error });
    }

    setIsLoading(false);
  };

  const handleMenuItemClick = ({ key, record }) => {
    switch (key) {
      case '0':
        history.push(`/team-members/edit/${record._id}`, '_parent');
        break;

      case '1':
        confirmOnUserBeforeReactivate(record);
        break;

      case '2':
        handleReInviteMember(record._id);
        break;

      case '3':
        confirmOnUserBeforeSuspend(record);
        break;

      case '4':
        confirmOnUserBeforeDelete(record);
        break;

      default:
        break;
    }
  };

  const actionsMenu = ({ record, isLoggedUser }) => {
    const isFirstAdmin = record.organizationalAccount;

    const editAction = {
      key: '0',
      label: formatMessage({
        id: 'team_members.actions.edit'
      }),
      disabled: isFirstAdmin
    };

    const deleteAction = {
      key: '4',
      label: formatMessage({
        id: 'team_members.actions.delete'
      }),
      danger: true,
      disabled: isFirstAdmin || isLoggedUser
    };

    // const suspendAction = HIDE_SUSPEND_MEMBER
    //   ? {}
    //   : {
    //       key: '3',
    //       label: formatMessage({
    //         id: 'team_members.actions.suspend'
    //       }),
    //       danger: true,
    //       disabled: isFirstAdmin || isLoggedUser
    //     };

    // const reactivateAction = HIDE_REACTIVATE_MEMBER
    //   ? {}
    //   : {
    //       key: '1',
    //       label: formatMessage({
    //         id: 'team_members.actions.reactivate'
    //       })
    //     };

    const divider = {
      type: 'divider'
    };

    const items = {
      [ACTIVE]: [
        editAction,
        ...(!HIDE_DELETE_MEMBER
          ? [
              divider,
              // suspendAction,
              deleteAction
            ]
          : [])
      ],
      [INVITATION_SENT]: [
        editAction,
        {
          key: '2',
          label: formatMessage({
            id: 'team_members.actions.reinvite'
          })
        },
        ...(!HIDE_DELETE_MEMBER ? [divider, deleteAction] : [])
      ],
      [EMAIL_VERIFIED]: [
        editAction,
        {
          key: '2',
          label: formatMessage({
            id: 'team_members.actions.reinvite'
          })
        },
        ...(!HIDE_DELETE_MEMBER ? [divider, deleteAction] : [])
      ],
      [SUSPENDED]: [
        editAction,
        // reactivateAction,
        ...(!HIDE_DELETE_MEMBER ? [divider, deleteAction] : [])
      ]
    };

    return (
      <Menu
        className="br-team-members-table__actions-menu"
        onClick={({ key }) => handleMenuItemClick({ key, record })}
        items={items[record.state?.code]}
      />
    );
  };

  const acceptMethods = (refreshMethod) => {
    refreshTable.current = refreshMethod;
  };

  const columns = [
    {
      title: formatMessage({ id: 'team_members.table.columns.name' }),
      render: ({ profile, organizationalAccount }) => {
        const isFirstAdmin = organizationalAccount;

        return (
          <>
            <span>{getFullName(profile)}</span>
            {isFirstAdmin && (
              <Tag className="br-tag br-tag-first-admin">
                {formatMessage({ id: 'team_members.table.first_admin' })}
              </Tag>
            )}
          </>
        );
      }
    },
    {
      dataIndex: 'emails',
      title: formatMessage({ id: 'team_members.table.columns.email' }),
      render: (emails) => emails?.[0]?.address
    },
    {
      dataIndex: 'totalSllrPermissionsAccessType',
      title: formatMessage({ id: 'team_members.table.columns.permissions' }),
      render: (totalSllrPermissionsAccessType) =>
        MEMBER_PERMISSIONS_NAME[totalSllrPermissionsAccessType]
    },
    {
      dataIndex: 'state',
      title: formatMessage({ id: 'team_members.table.columns.status' }),
      render: renderTag
    },
    ...(HIDE_ACTIONS_MENU
      ? []
      : [
          {
            width: 50,
            render: (record) => {
              const isLoggedUser = record._id === userId;

              return (
                <Dropdown
                  trigger={['click']}
                  overlay={actionsMenu({ record, isLoggedUser })}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement.parentElement.parentElement
                  }
                  disabled={isLoggedUser}
                >
                  <ActionsIcon
                    className={classnames('actions-icon', {
                      disabled: isLoggedUser
                    })}
                  />
                </Dropdown>
              );
            }
          }
        ])
  ];

  return (
    <BRTable
      className="br-team-members-table"
      title={formatMessage(
        {
          id: `team_members.table.${
            count === 1 ? 'title_singular' : 'title_plural'
          }`
        },
        {
          count
        }
      )}
      columns={columns}
      listFunction={getTableData}
      shareMethods={acceptMethods}
      showFilter={false}
    />
  );
};

export default TeamMembersTable;
